"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { ANALYTICS_EVENTS } from "@shared/analytics/events";
import type { FaqAskRefusal } from "@shared/faq/ask-protocol";
import { track } from "@/lib/analytics/tracker";
import { askFaq, type FaqAskOutcome } from "@/lib/faq/ask-faq";
import { randomId } from "@/lib/random-id";

export type AskedQuestion = {
  id: string;
  question: string;
  answer: string;
  state: "streaming" | "answered" | "not_on_listing" | "no_answer" | "failed" | FaqAskRefusal;
  questionId?: string;
};

function settledState(
  outcome: FaqAskOutcome,
): Exclude<AskedQuestion["state"], "streaming"> {
  if (outcome.kind === "refused") return outcome.refusal;
  if (outcome.kind === "failed") return "failed";
  return outcome.done.outcome;
}

type UpdateAsked = (
  id: string,
  change: (entry: AskedQuestion) => AskedQuestion,
) => void;

async function runAsk(args: {
  id: string;
  question: string;
  pagePath: string;
  signal: AbortSignal;
  update: UpdateAsked;
}): Promise<void> {
  const { id, update } = args;
  const outcome = await askFaq({
    question: args.question,
    pagePath: args.pagePath,
    signal: args.signal,
    onDelta: (text) =>
      update(id, (entry) => ({ ...entry, answer: entry.answer + text })),
    onReplace: (text) => update(id, (entry) => ({ ...entry, answer: text })),
  }).catch((): FaqAskOutcome => ({ kind: "failed" }));
  if (args.signal.aborted) return;

  const state = settledState(outcome);
  const done = outcome.kind === "answered" ? outcome.done : undefined;
  update(id, (entry) => ({
    ...entry,
    state,
    questionId: done?.questionId,
  }));
  track(ANALYTICS_EVENTS.FAQ_QUESTION_ASKED, {
    page_path: args.pagePath,
    outcome: state,
  });
}

export function useFaqAsk() {
  const pagePath = usePathname() ?? "/";
  const [asked, setAsked] = useState<AskedQuestion[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const update = useCallback<UpdateAsked>(
    (id, change) => {
      setAsked((entries) =>
        entries.map((entry) => (entry.id === id ? change(entry) : entry)),
      );
    },
    [],
  );

  const ask = useCallback(
    async (question: string) => {
      const id = randomId();
      const controller = new AbortController();
      abortRef.current = controller;
      setAsked((entries) => [
        ...entries,
        { id, question, answer: "", state: "streaming" },
      ]);

      await runAsk({
        id,
        question,
        pagePath,
        signal: controller.signal,
        update,
      });
    },
    [pagePath, update],
  );

  const isStreaming = asked.some((entry) => entry.state === "streaming");
  return { asked, ask, isStreaming };
}
